import React, { useState, useEffect, useCallback } from 'react';
import { taskTypesApi, TaskType } from '../../api/taskTypesApi';
import { Card } from '../../components/common/Card';
import { Button } from '../../components/common/Button';
import { Icons } from '../../components/common/Icons';
import './TaskTypes.css';

export function TaskTypes() {
  const [taskTypes, setTaskTypes] = useState<TaskType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  
  // Filter state
  const [showInactive, setShowInactive] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [allCategories, setAllCategories] = useState<string[]>([]);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingTask, setEditingTask] = useState<TaskType | null>(null);
  const [saving, setSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    category: '',
    description: '',
    is_leave_type: false,
  });

  const fetchTaskTypes = useCallback(async () => {
    try {
      setLoading(true);
      const response = await taskTypesApi.getTaskTypes({
        include_inactive: showInactive || undefined,
        category: categoryFilter || undefined,
      });
      // When showInactive is checked, only show inactive tasks
      if (showInactive) {
        setTaskTypes(response.tasks.filter(t => !t.is_active));
      } else {
        setTaskTypes(response.tasks);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load task types');
    } finally {
      setLoading(false);
    }
  }, [showInactive, categoryFilter]);

  // Fetch all categories once on mount (unfiltered) so the dropdown always has all options
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await taskTypesApi.getTaskTypes({ include_inactive: true });
        const cats = Array.from(new Set(response.tasks.map(t => t.category).filter((c): c is string => c !== null)));
        setAllCategories(cats);
      } catch {
        // will fall back to empty categories
      }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    fetchTaskTypes();
  }, [fetchTaskTypes]);

  // Auto-dismiss notifications after 5 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Categories for the filter dropdown (from initial full load)
  const categories = allCategories;

  const handleAddTask = () => {
    setEditingTask(null);
    setFormData({
      name: '',
      code: '',
      category: '',
      description: '',
      is_leave_type: false,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleEditTask = (task: TaskType) => {
    setEditingTask(task);
    setFormData({
      name: task.name,
      code: task.code || '',
      category: task.category || '',
      description: task.description || '',
      is_leave_type: task.is_leave_type,
    });
    setFormErrors({});
    setShowModal(true);
  };

  const handleDeleteTask = async (task: TaskType) => {
    if (!window.confirm(`Are you sure you want to delete "${task.name}"?`)) {
      return;
    }

    try {
      await taskTypesApi.deleteTaskType(task.id);
      setSuccess(`Task type "${task.name}" deleted successfully`);
      fetchTaskTypes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete task type');
    }
  };

  const handleToggleActive = async (task: TaskType) => {
    try {
      await taskTypesApi.updateTaskType(task.id, { is_active: !task.is_active });
      setSuccess(`Task type "${task.name}" ${task.is_active ? 'deactivated' : 'activated'}`);
      fetchTaskTypes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task type');
    }
  };

  const handleSeedDefaults = async () => {
    try {
      setLoading(true);
      const response = await taskTypesApi.seedTaskTypes();
      setSuccess(response.message);
      fetchTaskTypes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to seed task types');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errors: Record<string, string> = {};
    if (!formData.name.trim()) errors.name = 'Task name is required.';
    else if (formData.name.length > 60) errors.name = 'Task name must be 60 characters or fewer.';
    if (formData.category && formData.category.length > 20) errors.category = 'Category must be 20 characters or fewer.';
    if (Object.keys(errors).length > 0) { setFormErrors(errors); return; }
    setFormErrors({});
    setSaving(true);
    setError(null);

    try {
      if (editingTask) {
        await taskTypesApi.updateTaskType(editingTask.id, formData);
        setSuccess(`Task type "${formData.name}" updated successfully`);
      } else {
        await taskTypesApi.createTaskType(formData);
        setSuccess(`Task type "${formData.name}" created successfully`);
      }
      setShowModal(false);
      setFormErrors({});
      fetchTaskTypes();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save task type');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="task-types-page">
      <header className="task-types-header">
        <div className="task-types-title">
          <h1>Task Types</h1>
          <p>Manage task and leave types for timesheet entries</p>
        </div>
        <div className="header-actions">
          <Button variant="secondary" onClick={handleSeedDefaults}>
            <Icons.Refresh /> Seed Defaults
          </Button>
          <Button variant="primary" onClick={handleAddTask}>
            <Icons.Plus /> Add Task Type
          </Button>
        </div>
      </header>

      {error && (
        <div className="alert alert-error">
          <Icons.AlertCircle />
          <span>{error}</span>
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          <Icons.Check />
          <span>{success}</span>
          <button onClick={() => setSuccess(null)}>&times;</button>
        </div>
      )}

      <Card>
        <div className="task-types-filters">
          <div className="filter-group">
            <label>Category:</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat!}>
                  {cat}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group">
            <label>
              <input
                type="checkbox"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              Show Inactive
            </label>
          </div>
        </div>

        <div className="task-types-stats">
          <span><strong>{taskTypes.length}</strong> Total</span>
          <span><strong>{taskTypes.filter(t => t.is_active).length}</strong> Active</span>
          <span><strong>{taskTypes.filter(t => t.is_leave_type).length}</strong> Leave Types</span>
        </div>

        <div className="task-types-table-wrapper">
          <table className="task-types-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Category</th>
                <th>Type</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {taskTypes.length === 0 ? (
                <tr>
                  <td colSpan={6} className="no-data">
                    <div className="empty-state">
                      <Icons.List />
                      {showInactive ? (
                        <p>No inactive task types found</p>
                      ) : (
                        <>
                          <p>No task types found</p>
                          <Button variant="primary" onClick={handleSeedDefaults}>
                            Seed Default Task Types
                          </Button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                taskTypes.map((task) => (
                  <tr key={task.id} className={!task.is_active ? 'inactive' : ''}>
                    <td className="name-cell">
                      <div className="task-name">{task.name}</div>
                      {task.description && (
                        <div className="task-description">{task.description}</div>
                      )}
                    </td>
                    <td>
                      {task.code && <span className="code-badge">{task.code}</span>}
                    </td>
                    <td>{task.category || '-'}</td>
                    <td>
                      {task.is_leave_type ? (
                        <span className="badge badge-leave">Leave</span>
                      ) : (
                        <span className="badge badge-work">Work</span>
                      )}
                    </td>
                    <td>
                      <span className={`badge badge-${task.is_active ? 'active' : 'inactive'}`}>
                        {task.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <div className="actions-wrapper">
                        <button
                          className="action-btn edit"
                          onClick={() => handleEditTask(task)}
                          title="Edit"
                        >
                          <Icons.Edit />
                        </button>
                        <button
                          className={`action-btn ${task.is_active ? 'deactivate' : 'activate'}`}
                          onClick={() => handleToggleActive(task)}
                          title={task.is_active ? 'Deactivate' : 'Activate'}
                        >
                          {task.is_active ? <Icons.X /> : <Icons.Check />}
                        </button>
                        <button
                          className="action-btn delete"
                          onClick={() => handleDeleteTask(task)}
                          title="Delete"
                        >
                          <Icons.Trash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingTask ? 'Edit Task Type' : 'Add Task Type'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>
                <Icons.X />
              </button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-group">
                  <label htmlFor="name">Task Name *</label>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Development"
                    maxLength={60}
                  />
                  {formErrors.name && <span className="field-error">{formErrors.name}</span>}
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="code">Code</label>
                    <input
                      type="text"
                      id="code"
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g., DEV"
                      maxLength={10}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="category">Category</label>
                    <input
                      type="text"
                      id="category"
                      value={formData.category}
                      onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                      placeholder="e.g., Development"
                      maxLength={20}
                    />
                    {formErrors.category && <span className="field-error">{formErrors.category}</span>}
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="description">Description</label>
                  <textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Brief description of this task type"
                    rows={2}
                    maxLength={60}
                  />
                </div>

                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={formData.is_leave_type}
                      onChange={(e) => setFormData({ ...formData, is_leave_type: e.target.checked })}
                    />
                    <span>This is a leave type</span>
                  </label>
                  <small>Leave types are tracked separately for leave balance</small>
                </div>
              </div>

              <div className="modal-footer">
                <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={saving}>
                  {saving ? 'Saving...' : editingTask ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default TaskTypes;
